import { View, Platform } from 'react-native'
import { Image } from 'expo-image'
import { colors } from '@ui/colors'
import Icon from '@ui/elements/Icon'
import Typography from '@ui/typography'
import BlurEffect from '@ui/effects/BlurEffect'
import Gradient from '@ui/effects/Gradient'
import { optionGradient } from '@order/PricePackage/styles'
import {
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { PackagePointsTileProps } from '../types'
import {
  borderRadius,
  borderRadiusImage,
  colorOption,
  imageHeight,
} from '@order/Package/styles'

interface Props
  extends Pick<
    PackagePointsTileProps,
    'tier' | 'points' | 'image' | 'imageText' | 'imageProps' | 'discount'
  > {
  width: number
  height: number
  animation: SharedValue<number>
}

const BLUR = Platform.select({ default: 100, web: 60 })
const arrowSize = Platform.select({ default: 24, web: 32 })

export default function PackageCard(props: Props) {
  const {
    height,
    points,
    image,
    imageText,
    imageProps,
    width,
    tier = 'default',
    discount,
    animation,
  } = props

  const accentLeft = discount
    ? Platform.select({ default: 24, web: 48 })
    : width / 2 - width / 4

  const stylePressedOpacityFront = useAnimatedStyle(() => {
    return {
      opacity: interpolate(animation.value, [0, 1], [1, 0]),
    }
  })

  return (
    <View className="relative inset-0 flex h-full w-full flex-col">
      <View className="absolute h-full w-full overflow-hidden">
        <Gradient
          colors={[colors.white, colors.white]}
          start={[0.5, 0]}
          end={[0.5, 1]}
          style={[{ borderRadius }]}
        />
        <View
          className="absolute overflow-hidden"
          style={{
            width: width / 2,
            height: height / 2,
            left: accentLeft,
            bottom: 0,
            borderRadius,
          }}
        >
          <Gradient
            colors={optionGradient[colorOption[tier]]}
            start={[0.5, 0]}
            end={[0.5, 1]}
            style={{ borderRadius }}
          />
        </View>
        {Platform.select({
          default: (
            <BlurEffect
              tint="regular"
              intensity={BLUR}
              className="left-0 h-full"
              style={{ borderRadius }}
            />
          ),
          web: (
            <div
              className="absolute left-0 top-0 h-full w-full"
              style={{ backdropFilter: `blur(${BLUR}px)`, borderRadius }}
            />
          ),
        })}
        <Gradient
          colors={[colors.white, colors.white25]}
          start={[0.5, 0]}
          end={[0.5, 1]}
          style={[{ borderRadius }, stylePressedOpacityFront]}
        />
      </View>
      <View
        className="w-full overflow-hidden px-8"
        style={{
          height: imageHeight,
        }}
      >
        <Image
          source={image}
          contentFit="cover"
          cachePolicy="memory-disk"
          transition={300}
          contentPosition="center"
          style={[
            {
              height: '100%',
              width: 'auto',
              borderBottomLeftRadius: borderRadiusImage,
              borderBottomRightRadius: borderRadiusImage,
            },
          ]}
          {...imageProps}
        />
        {imageText && (
          <View
            className="absolute flex flex-row items-center justify-center"
            style={{ top: imageHeight / 2 - arrowSize / 2, left: 0, right: 0 }}
          >
            <Icon
              name="arrow-left"
              size={arrowSize}
              strokeWidth={4}
              color={colors.white}
            />
            <View
              className="flex flex-row items-center justify-center rounded-full bg-white px-4"
              style={{ height: arrowSize }}
            >
              <Typography
                variant={Platform.select({ default: 'XSB', web: 'SMB' })}
                color={colors.text}
                className="whitespace-nowrap text-center"
              >
                {imageText}
              </Typography>
            </View>
            <Icon
              name="arrow-right"
              size={arrowSize}
              strokeWidth={4}
              color={colors.white}
            />
          </View>
        )}
      </View>
      <View className="flex flex-1 flex-col gap-2 px-8 py-4">
        {points?.map((point, index) => (
          <View key={index} className="flex flex-row items-start gap-2">
            <View className="flex h-6 items-center justify-center">
              <Icon name="point-filled" size={12} color={colors.text} />
            </View>
            <Typography
              variant={Platform.select({ default: 'SM', web: 'MD' })}
              color={colors.text}
            >
              {point}
            </Typography>
          </View>
        ))}
      </View>
    </View>
  )
}
